import { useSyncExternalStore } from "react";
import {
	clearStoredAuth,
	getStoredAuth,
	isAuthValid,
	type StoredAuth,
} from "#/lib/api/token-storage";

const AUTH_EVENT = "tenbagger:auth-changed";

function subscribeAuth(onStoreChange: () => void): () => void {
	if (typeof window === "undefined") return () => {};
	const handler = () => onStoreChange();
	window.addEventListener(AUTH_EVENT, handler);
	window.addEventListener("storage", handler);
	return () => {
		window.removeEventListener(AUTH_EVENT, handler);
		window.removeEventListener("storage", handler);
	};
}

function getSnapshot(): StoredAuth | null {
	return getStoredAuth();
}

function getServerSnapshot(): StoredAuth | null {
	return null;
}

export function useStoredAuth(): StoredAuth | null {
	return useSyncExternalStore(subscribeAuth, getSnapshot, getServerSnapshot);
}

/**
 * Session reader. The session IS the identity: it's established directly by
 * the email-code login dialog (`/v1/auth/email/verify` → `setStoredAuth`), and
 * the wallet address travels inside it — there is no separate wallet
 * connection step and no client-side signing handshake. Logout is purely
 * client-side (the backend has no logout endpoint).
 */
export function useAuth() {
	const stored = useStoredAuth();

	const isAuthenticated = isAuthValid(stored);

	function logout(): void {
		clearStoredAuth();
	}

	return {
		token: isAuthenticated ? stored.access_token : null,
		userId: isAuthenticated ? stored.user_id : null,
		walletAddress: isAuthenticated ? stored.wallet_address : null,
		email: isAuthenticated ? stored.email : null,
		isAuthenticated,
		logout,
	};
}
